import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../services/api';
import { useGroup } from '../../contexts/GroupContext';
import { useNotifications } from '../../contexts/NotificationContext';
import NucleusPage from '../../components/nucleus/NucleusPage';
import RekeyNotice from '../../components/nucleus/RekeyNotice';
import { pathFor } from '../../nucleus/map';
import { useSpaceAccess } from '../../nucleus/access';
import Vault from '../../components/nucleus/Vault';
import { nameOf } from '../nucleus/PeopleData';
import { loadPluginList } from '../plugins/data/items';
import { plural } from '../../nucleus/format';
import { pluginRequestsPath } from '../../nucleus/attention';
import './HomeMap.css';

// HOME — Nucleus's front door: a line on what it is, then the vault (Vault.js),
// the three layers drawn as one full circle, every part a door with its live
// figures — internal (your Identity: the graph), gates (Realms & Contracts), external
// (People, Groups, Apps, Plugins). Closing every gate lives on Privacy &
// Access, where the gates are. THE CURRENT SPACE ONLY: in a group the
// figures are the group's, people are its members, and Groups stands down.

// A figure is undefined while it is read, a number (or a word) once read, or
// { error } when the read failed — never a silent zero.
const failed = (e) => ({ error: e?.message || 'Could not be read' });

const countOf = (groupId) => api.request(groupId
    ? `/groups/${encodeURIComponent(groupId)}/nodes/count`
    : '/nodes/count').then((r) => {
    if (typeof r?.count !== 'number') throw new Error('No count came back');
    return r.count;
});

const liveGrant = (ep, now) => ep && !ep.revoked_at && !(ep.expires_at && new Date(ep.expires_at).getTime() < now);

// Every figure Home shows, each read from the endpoint its page reads, for the
// space you are in. Returned as { key: promise }, so each cell fills as its
// own read lands and one slow or failed read costs only its own cell. ONLY
// what a door draws is read: a figure nobody sees still costs a request, and
// its failure would still count in the "could not be read" note.
export function figureReads(groupId, { isAdmin = true } = {}) {
    const g = groupId ? encodeURIComponent(groupId) : null;
    const now = Date.now();
    const grants = api.request(g ? `/groups/${g}/exposure-profiles` : '/exposure-profiles')
        // a group's grants to its own members are their write access, not contracts
        .then((r) => (r?.exposure_profiles || r?.profiles || []).filter((ep) => liveGrant(ep, now) && ep.entity_type !== 'group_member'));
    const realms = (groupId ? api.getGroupPrivacyRealms(groupId) : api.getPrivacyRealms())
        .then((r) => (Array.isArray(r) ? r : []).filter((x) => !x.is_null_realm));
    // the outside sheets show their items as well as their counts: one read each, shared
    const appList = api.request(`/app-shell/apps${g ? `?group_id=${g}` : ''}`).then((r) => {
        const seen = new Map();
        (r?.apps || []).forEach((row) => { const a = row?.app; if (a?.id && !seen.has(a.id)) seen.set(a.id, { id: String(a.id), label: a.name || 'An app', app: a }); });
        return [...seen.values()];
    });
    // installed means what the Plugins page means: installed in this space, as
    // its lifecycle says (data/items); those needing a hand counted apart
    const installed = loadPluginList(groupId).then((list) => list.filter((p) => p.installed));
    const pluginList = installed.then((list) => list.map((p) => ({ id: String(p.id), label: p.name || 'A plugin' })));
    const peopleList = groupId
        ? api.getGroupMembers(groupId).then((r) => (r?.members || []).map((m) => ({ id: String(m.user_id || m.id), label: nameOf(m.user || m), url: m.user?.avatar_url || m.avatar_url || null })))
        : api.getOutgoingFollows({ relationship_type: 'friend_request', status: 'accepted', limit: 200 }).then((r) => (r?.follows || []).map((f) => {
            const u = f.followed || {};
            return { id: String(u.id || f.followed_id), label: nameOf(u), url: u.avatar_url || null };
        }));
    const reads = {
        nodes: countOf(groupId),
        realms: realms.then((r) => r.length),
        apps: appList.then((l) => l.length),
        appItems: appList,
        plugins: pluginList.then((l) => l.length),
        pluginItems: pluginList,
        // a group's plugins wait on its admins
        ...(!groupId || isAdmin ? { pluginWants: installed.then((l) => l.filter((p) => p.needsYou).length) } : {}),
        // runs an app or agent asked for, waiting for a yes (a member's own in a
        // group); an older server without the route waits on nothing
        runsWaiting: api.request(pluginRequestsPath(groupId))
            .then((r) => (Array.isArray(r) ? r : r?.requests || []).filter((x) => (x?.status || 'pending') === 'pending').length)
            .catch(() => 0),
        people: peopleList.then((l) => l.length),
        peopleItems: peopleList,
        // a group's pending invitations are its admins' to read
        ...(!groupId ? { invitations: api.getIncomingInvitations().then((r) => (r?.invitations || []).filter((i) => i.status === 'pending').length) }
            : isAdmin ? { invitations: api.getGroupInvitations(groupId).then((r) => (r?.invitations || []).filter((i) => i.status === 'pending').length) } : {}),
        grants: grants.then((eps) => eps.length),
    };
    if (!groupId) {
        // asking to be friends, or to follow you (Open follows off)
        const asks = api.getIncomingFollows({ status: 'pending', limit: 200 }).then((r) => r?.follows || []);
        reads.requests = asks.then((l) => l.length);
        // friend requests wait on Friends, follow requests on Following
        reads.friendAsks = asks.then((l) => l.filter((f) => f.relationship_type === 'friend_request').length);
        const groupList = api.getGroups().then((r) => (r?.groups || (Array.isArray(r) ? r : [])).map((x) => ({ id: String(x.id), label: x.name || 'A group', url: x.avatar_url || x.avatar || null })));
        reads.groups = groupList.then((l) => l.length);
        reads.groupItems = groupList;
    }
    return reads;
}

export default function HomeMap() {
    const navigate = useNavigate();
    const { activeGroup, activeGroupId } = useGroup();
    const { unreadCount } = useNotifications();
    const groupId = activeGroupId || null;
    // a member reads no admin-only figure (the group's invitations)
    const access = useSpaceAccess();
    const isAdmin = !groupId || access.role === 'admin';
    const [values, setValues] = useState({});

    useEffect(() => {
        let live = true;
        setValues({});
        Object.entries(figureReads(groupId, { isAdmin })).forEach(([key, p]) => {
            p.then((v) => v, failed).then((v) => { if (live) setValues((cur) => ({ ...cur, [key]: v })); });
        });
        return () => { live = false; };
    }, [groupId, isAdmin]);

    const go = (section, page, query) => navigate(`${pathFor(section, page, groupId)}${query ? `?${query}` : ''}`);

    // THE VAULT'S DOORS — each with its own figures, read once above
    const num = (k) => (typeof values[k] === 'number' ? values[k] : undefined);
    // a door's headline: the number, a dash when its read failed, blank while it is read
    const fig = (k) => (typeof values[k] === 'number' ? values[k] : values[k]?.error ? '—' : undefined);
    // the word alone, when the figure beside it already says the number
    const noun = (n, one, many = `${one}s`) => (n === 1 ? one : many);
    const requests = num('requests') || 0;
    const invitations = num('invitations') || 0;
    const pluginWants = num('pluginWants') || 0;
    const runsWaiting = num('runsWaiting') || 0;
    // a line that wants you may name the page it opens (`why`, passed back on a press)
    const line = (text, tone, why) => (text ? { text, tone, ...(why ? { why } : {}) } : null);
    // a sheet's items: undefined while read, [] or the list once read
    const list = (k) => (Array.isArray(values[k]) ? values[k] : undefined);
    const vault = {
        // the centre is one thing: you — your identity graph
        core: [
            // a member with No access reads none of the group's graph: said, not
            // counted, in two short lines the figure has room for
            access.noAccess
                ? { id: 'graph', word: 'Identity', glyph: 'graph', n: undefined, lines: [line('No access yet'), line('Ask an admin')] }
                : { id: 'graph', word: 'Identity', glyph: 'graph', n: fig('nodes'), lines: [
                    line(num('nodes') != null ? `${noun(num('nodes'), 'thing')} in ${groupId ? `${activeGroup?.name || 'this group'}’s` : 'your'} graph` : null),
                ] },
        ],
        // one band, undivided: what may pass, and who it has been granted to
        gates: [
            { id: 'gates', word: 'Realms & Contracts', glyph: 'realms', n: undefined,
                lines: [
                line(num('realms') != null ? plural(num('realms'), 'realm') : null),
                line(num('grants') != null ? plural(num('grants'), 'contract') : null),
            ] },
        ],
        outside: [
            groupId
                ? { id: 'people', word: 'Members', glyph: 'people', n: fig('people'), lines: [line('in this group')], items: list('peopleItems'), kind: 'person', empty: 'No members yet' }
                : { id: 'people', word: 'People', glyph: 'people', n: fig('people'), lines: [line(num('people') != null ? noun(num('people'), 'friend') : null), line(requests ? `${requests} asking` : null, 'warn')], items: list('peopleItems'), kind: 'person', empty: 'No friends yet' },
            groupId
                // an admin sees who is invited; a member, in the same place, what they bring in
                ? (isAdmin
                    ? { id: 'groups', word: 'Invited', glyph: 'groups', n: fig('invitations'), lines: [line('waiting to join')] }
                    : { id: 'share', word: 'Your share', glyph: 'self', n: undefined, lines: [line('what you bring into this group')] })
                : { id: 'groups', word: 'Groups', glyph: 'groups', n: fig('groups'), lines: [line(num('groups') != null ? noun(num('groups'), 'group') : null), line(invitations ? `${invitations} inviting you` : null, 'warn')], items: list('groupItems'), kind: 'group', empty: 'No groups yet' },
            { id: 'apps', word: 'Apps', glyph: 'apps', n: fig('apps'), lines: [line('connected')], items: list('appItems'), kind: 'app', empty: 'No apps connected' },
            { id: 'plugins', word: 'Plugins', glyph: 'plugins', n: fig('plugins'), lines: [
                line('set up'),
                // what wants you: plugins with a step open (My plugins), and runs waiting for your yes (Runs)
                line(pluginWants ? `${pluginWants} need${pluginWants === 1 ? 's' : ''} you` : null, 'warn', 'installed'),
                line(runsWaiting ? `${plural(runsWaiting, 'run')} waiting` : null, 'warn', 'runs'),
            ], items: list('pluginItems'), kind: 'plugin', empty: 'No plugins set up' },
        ].filter(Boolean),
        open: {
            graph: () => go('graph'),
            self: () => go('self'),
            activity: () => go('activity', unreadCount ? 'inbox' : null),
            gates: () => go('realms'),
            // a sheet opens its section; only its warning line ("2 asking", "2 inviting
            // you") opens the page that waits on you
            people: (why) => go('people', groupId ? 'members' : (why === 'warn' && requests && !num('friendAsks') ? 'following' : null)),
            groups: (why) => (groupId ? go('people', 'invitations') : go('groups', why === 'warn' && invitations ? 'invitations' : null)),
            apps: () => go('apps'),
            plugins: (why) => go('plugins', why === 'runs' ? 'runs' : null),
            share: () => go('self', 'share'),
        },
    };
    // whose vault: yours, or the group's by name
    const vaultLabel = groupId ? `${activeGroup?.name || 'This group'}’s vault` : 'Your vault';
    const failures = Object.values(values).filter((v) => v && typeof v === 'object' && v.error).length;

    return (
        <NucleusPage
            bodyClassName="home-body"
            title={activeGroup?.name || (groupId ? 'This group' : 'Self')}
            notice={groupId ? <RekeyNotice /> : null}
        >
            {/* your world: exactly one screen */}
            <div className="home-segment">
            <div className="home-stages">

                <section className="home-gates" aria-label={vaultLabel}>
                    <div className="home-gates__stage">
                        <Vault label={vaultLabel} core={vault.core} gates={vault.gates} outside={vault.outside} onPress={(id, why) => vault.open[id]?.(why)}
                            onItem={(section, item) => go(section === 'people' && groupId ? 'people' : section, section === 'people' && groupId ? 'members' : null, `open=${encodeURIComponent(item.id)}`)} />
                    </div>
                </section>
                {failures > 0 && (
                    <p className="home-stages__note" role="status">
                        {failures === 1 ? 'One figure' : `${failures} figures`} could not be read. They show a dash; their pages still open.
                    </p>
                )}
            </div>
            </div>
        </NucleusPage>
    );
}
